import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Line } from 'recharts';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';


const org = "devtest";
const token = "uuilq1SPWzFeaPtbrWDeZAVLrW-ixagpuawbyGmST3nwsM02QeEri9Pe-nzjEX_RufTaweOxsNfmfxV_hCudUA==";
const url = "https://us-east-1-1.aws.cloud2.influxdata.com";

const headerMappings = {
    carbon_intensity: "Carbon Intensity",
    fossil_fuel_percentage: "Fossil Fuel Percentage"
};

const intervalMapping = {
    1: 2,  // Every 2 hours for 1 day
    3: 24, // Daily for 3 days
    7: 24, // Daily for 7 days
    14: 3 * 24, // Every 3 days for 14 days
    30: 7 * 24, // Every 7 days for 30 days
};

function Graph({ type, region, onClose }) {
    const [data, setData] = useState([]);
    const [timeRange, setTimeRange] = useState(7); // Default to 7 days

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.post(`${url}/api/v2/query?org=${org}`,
                    {
                        query: `
                            from(bucket: "co2signaldata")
                            |> range(start: -${timeRange}d)
                            |> filter(fn: (r) => r["_measurement"] == "CO2Signal")
                            |> filter(fn: (r) => r["_field"] == "${type}")
                            |> filter(fn: (r) => r["location"] == "${region}")
                            |> aggregateWindow(every: 1h, fn: mean, createEmpty: false)
                            |> yield(name: "mean")
                        `,
                        type: 'flux',
                    },
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Token ${token}`,
                        },
                    }
                );

                const csvData = response.data;
                const rows = csvData.split('\n').slice(1, -1); // Exclude the last row which is empty
                const parsedData = rows.map(row => {
                    const columns = row.split(',');
                    return {
                        time: columns[5],
                        value: parseFloat(columns[6]),
                    };
                }).filter(data => !isNaN(data.value)); // Remove rows with NaN values

                setData(parsedData);

            } catch (error) {
                console.error('Error fetching data from InfluxDB:', error);
            }
        };

        fetchData();
    }, [type, region, timeRange]); // Add timeRange as a dependency

    const handleTimeRangeChange = (event) => {
        setTimeRange(event.target.value);
    };

    return (
        <Dialog
            open={!!type}
            onClose={onClose}
            maxWidth="md"
            fullWidth={true}
            style={{ maxWidth: '100%' }}
        >
            <DialogTitle style={{ backgroundColor: '#222', color: '#ccc', padding: '10px' }}>
               <b>{headerMappings[type]}  for {region} </b>
            </DialogTitle>
            <DialogContent
                style={{
                    backgroundColor: '#222',
                    padding: '10px',
                    overflow: 'hidden',  // Hide the scrollbar
                    width: '100%',  // Set width to 100% to occupy the full space
                    height: '100%',  // Set height to 100% to occupy the full space
                    borderRadius: '8px',  // Add border-radius for a softer edge
                    color: '#ccc',  // Set text color to match with the title
                }}
            >
                <div style={{ marginBottom: '10px' }}>
                    <label>
                        <b>Time Range : </b>
                        <select onChange={handleTimeRangeChange} value={timeRange}
                                style={{
                                    backgroundColor: '#333', // Set the background color to match the dialog content
                                    color: '#ccc',  // Set text color to match the dialog content
                                    border: '1px solid #444',  // Set border color to a slightly lighter shade
                                    borderRadius: '4px',  // Optional: add a small border radius
                                }}>
                            <option value={1}>1 Day</option>
                            <option value={3}>3 Days</option>
                            <option value={7}>7 Days</option>
                            <option value={14}>14 Days</option>
                            <option value={30}>30 Days</option>
                        </select>
                    </label>
                </div>
                <LineChart
                    width={820}  // Revert to numeric value
                    height={500}
                    data={data}
                    margin={{
                        top: 10,
                        right: 10,
                        left: 20,
                        bottom: 10,
                    }}
                    style={{
                        backgroundColor: '#222',
                        borderRadius: '15px',
                        padding: '10px',
                    }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#3a3a3a" />
                    <XAxis
                        dataKey="time"
                        stroke="#ccc"
                        tick={{ fontSize: 11 }}  // Adjust the font size of the tick labels
                        interval={intervalMapping[timeRange]} // Set interval based on timeRange state
                        tickFormatter={(timeStr, index) => {
                            if (index === 0) return '';  // Hide the first tick label
                            const date = new Date(timeStr);
                            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                        }}
                        tickLine={{ transform: 'translate(0, 5)' }} // Adjust the position of the ticks and labels
                    />
                    <YAxis
                        dataKey="value"
                        stroke="#ccc"
                        tickFormatter={(value, index) => {
                            if (index === 0) return '';  // Hide the first tick label
                            return value + (type === 'fossil_fuel_percentage' ? "%" : "gCO2/kWh");
                        }}
                        tick={{ fontSize: 10 }}  // Adjust the font size of the tick labels
                        tickLine={{ transform: 'translate(0, 5)' }} // Adjust the position of the ticks and labels
                    />
                    <Tooltip
                        contentStyle={{
                            backgroundColor: '#333',
                            border: 'none',
                        }}
                        itemStyle={{
                            color: '#aaa',
                        }}
                        labelFormatter={(value) => {
                            const date = new Date(value);
                            return `Time: ${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')} ${String(date.getHours()).padStart(2, '0')}:00`;
                        }}
                        formatter={(value) => value + (type === 'fossil_fuel_percentage' ? "%" : " gCO2/kWh")}
                    />
                    <Line type="monotone" dataKey="value"   dot={false} stroke="#03c04a" activeDot={{ r: 8 }} />
                </LineChart>
            </DialogContent>
        </Dialog>

    );
}

export default Graph;
